import React, { ChangeEvent, useState, useEffect } from "react";
import { MultipleFileUploadComponent } from "components/form/MultipleFileUpload";
import { BasePopup } from "components/pop-up/BasePopup";
import { Referral } from "types";
import {
  fetchProsecutorDecisionTypesList,
  updateAssetReferral,
} from "services/assetRecoveryService";
import { useTooltip } from "context/TooltipProvider";
import { useTranslation } from "react-i18next";

interface AssetRecoveryPopupProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit?: (e: React.FormEvent) => void;
  isEditMode?: boolean;
  existingData?: any;
  referralId: string;
  referral: Referral | null;
  onDecsionCreated: (res: any) => void;
  setActiveTab?: React.Dispatch<React.SetStateAction<string>>;
  assetRecoveryFiles?: File[];
  setAssetRecoveryFiles?: React.Dispatch<React.SetStateAction<File[]>>;
}

const defaultFormData = {
  prosecutor_decision_on_asset_recovery: "",
  asset_reovery_charge: "",
  asset_recovery_request_date: "",
};

export const AddAssetRecoveryPopup: React.FC<AssetRecoveryPopupProps> = ({
  isOpen,
  onClose,
  onSubmit,
  isEditMode = false,
  existingData,
  referralId,
  setActiveTab,
  onDecsionCreated,
  referral,
  assetRecoveryFiles,
  setAssetRecoveryFiles,
}) => {
  const [loading, setLoading] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);
  const [formData, setFormData] = useState(defaultFormData);
  const [prosecutorDecisionTypes, setProsecutorDecisionTypes] = useState<
    { id: string; name: string }[]
  >([]);
  const { showTooltip } = useTooltip();
  const { t } = useTranslation();

  const hasExistingData =
    existingData &&
    (existingData.prosecutor_decision_on_asset_recovery ||
      existingData.asset_reovery_charge ||
      existingData.asset_recovery_request_date);
    // ||
    // (existingData.asset_recovery_request_document &&
    //  existingData.asset_recovery_request_document.length > 0)

  const actualEditMode = isEditMode || hasExistingData;

  useEffect(() => {
    if (isOpen) {
      if (actualEditMode && existingData) {
        setFormData({
          prosecutor_decision_on_asset_recovery:
            existingData.prosecutor_decision_on_asset_recovery || "",
          asset_reovery_charge: existingData.asset_reovery_charge || "",
          asset_recovery_request_date:
            existingData.asset_recovery_request_date || "",
        });

        // if (existingData.asset_recovery_request_document &&
        //     existingData.asset_recovery_request_document.length > 0) {

        //   // setUploadedFiles(existingData.asset_recovery_request_document);
        // }
      } else {
        // Reset form for new entry
        console.log("Initializing create mode - no existing data found");
        resetForm();
      }
    }
  }, [isOpen, actualEditMode, existingData]);

  useEffect(() => {
    fetchProsecutorDecisionTypesList()
      .then((data) => {
        setProsecutorDecisionTypes(data);
      })
      .catch((error) => {
        console.error("Failed to fetch prosecutor decision types:", error);
      });
  }, []);
  const resetForm = () => {
    setFormData(defaultFormData);
    setUploadedFiles([]);
  };

  const handleInputChange = (
    e: ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>,
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validation
    if (!formData.prosecutor_decision_on_asset_recovery.trim()) {
      showTooltip(t("please_enter_prosecutor_decision"), "error");
      return;
    }

    if (!formData.asset_reovery_charge.trim()) {
      showTooltip(t("please_enter_asset_recovery_charge"), "error");
      return;
    }

    if (!formData.asset_recovery_request_date) {
      showTooltip(t("please_select_asset_recovery_date"), "error");
      return;
    }

    setLoading(true);

    try {
      const data = new FormData();

      // Append form data
      data.append(
        "prosecutor_decision_on_asset_recovery",
        formData.prosecutor_decision_on_asset_recovery,
      );
      data.append(
        "asset_recovery_request_date",
        formData.asset_recovery_request_date,
      );
      data.append("asset_reovery_charge", formData.asset_reovery_charge);

      // Set appropriate status based on edit mode
      if (actualEditMode) {
        data.append("action_status", "Asset Recovery Updated");
        data.append("forward_status", "Forwarded to Coordinator");
      } else {
        data.append("action_status", "Asset Recovery Requested");
        data.append("forward_status", "Forwarded to Coordinator");
      }

      // Append files
      if (uploadedFiles.length > 0) {
        uploadedFiles.forEach((file) => {
          data.append("asset_recovery_request_document", file);
        });
      }

      // Update the referral with decision data
      const response = await updateAssetReferral(referralId, data);

      const successMessage = actualEditMode
        ? t("asset_recovery_updated_successfully")
        : t("asset_recovery_created_successfully");

      showTooltip(successMessage, "success");
      onClose();
      resetForm();

      if (onDecsionCreated && response) {
        onDecsionCreated(response);
        setActiveTab?.("Decision");
      }
    } catch (error) {
      const errorMessage = actualEditMode
        ? t("failed_to_update_asset_recovery")
        : t("failed_to_create_asset_recovery");
      showTooltip(errorMessage, "error");
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };
  return (
    <BasePopup
      isOpen={isOpen}
      onClose={handleClose}
      title={actualEditMode ? "Edit Asset Recovery" : "Add Asset Recovery"}
      description={
        actualEditMode
          ? "Edit asset recovery information"
          : "Add asset recovery information"
      }
    >
      <form onSubmit={handleSubmit}>
        <div className="flex flex-col">
          <label className="referral-popup-label">Prosecutor Decision *</label>

          <select
            className="referral-form-field mt-2"
            name="prosecutor_decision_on_asset_recovery"
            value={formData.prosecutor_decision_on_asset_recovery}
            onChange={handleInputChange}
            required
          >
            <option value="">Select Decision Type</option>
            {prosecutorDecisionTypes.map((type) => (
              <option key={type.id} value={type.name}>
                {type.name}
              </option>
            ))}
          </select>
        </div>

        <div className="flex flex-col mt-4">
          <label className="referral-popup-label">
            Asset Recovery Charge *
          </label>

          <textarea
            className="referral-form-field h-24"
            onChange={handleInputChange}
            name="asset_reovery_charge"
            placeholder="Enter asset recovery charge details"
            value={formData.asset_reovery_charge}
            required
          />
        </div>

        <div className="flex flex-col mt-4">
          <label className="referral-popup-label">Asset Recovery Date *</label>
          <input
            className="referral-form-field"
            onChange={handleInputChange}
            name="asset_recovery_request_date"
            type="date"
            value={formData.asset_recovery_request_date}
            required
          />
        </div>

        <MultipleFileUploadComponent
          uploadedFiles={uploadedFiles as File[]}
          onFilesChange={setUploadedFiles}
          label="Asset Recovery Request Documents"
        />

        <button
          type="submit"
          disabled={loading}
          className={`mt-6 mb-4 referral-form-button text-white w-full ${
            loading ? "opacity-50 cursor-not-allowed" : ""
          }`}
        >
          {loading
            ? "Processing..."
            : actualEditMode
              ? "Update Asset Recovery"
              : "Add Asset Recovery"}
        </button>
      </form>
    </BasePopup>
  );
};
